import type { SpriteName } from "./assets";
import type { Finger, Theme } from "./themes";
import { successMessages, failMessages } from "./messages";
import { UI, type Lang, type UIStrings } from "./i18n";
import type { Mode as GameMode } from "./modes";
import { speedFactor, precisionPoints, transformPoint } from "./mechanics";
import { sfx } from "../sound";
import {
  W,
  TOLERANCE,
  PAUSE_OPT_Y,
  PAUSE_BTN,
  type PixiRenderer,
  type RenderState,
} from "./renderer";

// La mise en page du nez (recadrage, position, narines) vient du thème (themes.ts).
// Tout le reste ci-dessous est partagé entre les thèmes.

// Plage balayée par la pointe principale lors du va-et-vient.
const SWEEP_MIN = 25;
const SWEEP_MAX = 215;
// (TOLERANCE, W, H et les repères de pause viennent de renderer.ts — partagés.)

const POKE_DURATION = 0.9; // secondes pour l'aller-retour du doigt
// Sur un raté, le doigt monte plus haut SUR le nez (px au-dessus de la narine) et
// reste dessiné par-dessus le nez (il s'écrase sur la surface au lieu d'entrer).
const MISS_RISE = 18;
const COOLDOWN = 0.2; // délai après un coup où l'action est bloquée (la main continue)
const COUNTDOWN = 3; // décompte 3·2·1 au début de partie (s)
const COUNTDOWN_RESUME = 2; // décompte plus court à la reprise après pause (s)
const CONTINUE_TIME = 15; // secondes accordées en revivant la partie (« Continue »)
const START_SPEED = 80; // px/s
const SPEED_STEP = 6; // accélération à chaque réussite
const MAX_SPEED = 170;
// Le défilement accélère avec le temps. Courbe quadratique calée sur 3 points :
//   début (30 s restant)  → ×SPEED_START
//   milieu (15 s restant) → ×SPEED_MID
//   fin (0 s restant)     → ×SPEED_END
// Le creux entre début et milieu rend la montée douce d'abord, plus raide ensuite.
const SPEED_START = 1;
const SPEED_MID = 1.2;
const SPEED_END = 2.2; // était 2.9 : la fin de partie devenait injouable
// Score : chaque réussite = précision (1 OK / 2 BIEN / 3 PARFAIT selon la justesse
// au centre de la narine) × multiplicateur de série. Le combo monte à chaque
// réussite d'affilée et retombe sur un raté.
const MAX_COMBO = 8; // plafond du multiplicateur de série
const NOSE_SWAY_PERIOD = 4; // s pour un aller-retour du nez (mode Nez mobile)
const NOSE_TILT_PERIOD = 3.5; // s pour un cycle de tangage/zoom (mode Nez pivotant)
const NOSE_PIVOT_Y = 14; // y (logique) du pivot : haut-milieu du nez

const MSG_TIME = 1.8; // durée d'affichage du message de réussite

// Écran de fin de partie : rendu en HTML/CSS par game-screen (overlay au-dessus
// du canvas) — le moteur se contente d'exposer l'état (isOver, score, record…).

type Mode = "run" | "poke";

export class Game {
  private mode: Mode = "run";
  private originX = 0; // x du bord gauche du sprite (défini dans le constructeur)
  private dir = 1; // sens du défilement : +1 droite, -1 gauche
  private speed = START_SPEED;
  private fingerIndex = 0;

  private pokeElapsed = 0;
  private pokeOriginX = 0;
  private pokeFxDone = false; // particules d'impact déjà émises pour ce coup
  private pokeFreezeE = 0; // temps figé pendant le coup (oscillations du nez verrouillées)
  private hit = false;
  private cooldown = 0; // pause pendant laquelle la main est figée et les clics ignorés
  private countdown = COUNTDOWN; // décompte avant de pouvoir jouer (3·2·1)
  // Boosters (une seule manche : remis à zéro par reset()).
  private slowMo = false; // main -20 %
  private shield = 0; // ratés pardonnés restants
  private boostIcon: string | null = null; // icône HUD du booster actif (🐌/💰/🛡️)

  private score = 0; // points (précision × combo cumulés)
  private misses = 0;
  private streak = 0; // réussites consécutives
  private combo = 1; // multiplicateur courant = min(MAX_COMBO, streak)
  private gainLine = ""; // détail du dernier gain (ex. "PARFAIT +9 ×3")
  private best = 0; // meilleur score du thème (persisté en localStorage)
  private newRecord = false; // la partie en cours a battu le record
  private recordFlashed = false; // flash « RECORD ! » déjà émis cette partie
  private timeLeft = 0; // temps restant (s) ; défini par le mode au constructeur/reset
  private elapsed = 0; // temps écoulé (s) — pilote l'accélération
  private won = false; // partie gagnée (objectif de série atteint)
  private over = false; // partie terminée
  private exit = false; // le joueur demande le retour au choix des styles
  private paused = false; // partie en pause (overlay menu sur le canvas)
  private pauseSel = 0; // option surlignée du menu pause
  private msg = ""; // message (réussite ou échec) en cours
  private msgTimer = 0;
  private msgFail = false; // true → message d'échec (rouge), false → réussite (vert)
  private lastMsg = -1; // évite de répéter le même message de réussite d'affilée
  private lastFail = -1; // idem pour les messages d'échec

  // Langue : chaînes d'UI + listes de messages du thème, dans la langue active.
  private readonly ui: UIStrings;
  private readonly successMsgs: string[];
  private readonly failMsgs: string[];

  // Échelle des doigts, calée pour que l'écart des 2 pointes (24 px natifs)
  // corresponde pile à l'écart des narines du thème → le doigt double s'aligne.
  private readonly fingerScale: number;

  constructor(
    // Couche de rendu PixiJS (scène retenue) : le moteur lui envoie un RenderState.
    private readonly rend: PixiRenderer,
    private readonly theme: Theme,
    lang: Lang,
    private readonly gameMode: GameMode,
    // Appelé une fois à chaque fin de partie (pour soumettre le score à l'API).
    private readonly onGameOver?: (r: { score: number; theme: string; mode: string }) => void,
  ) {
    // Échelle calée pour que l'écart des 2 pointes du doigt double corresponde
    // pile à l'écart des narines → le doigt double s'aligne sur les deux narines.
    const dbl = theme.fingers.find((f) => f.tips.length === 2)!;
    const tipSpacing = dbl.tips[1]! - dbl.tips[0]!;
    this.fingerScale = (theme.nostrilR - theme.nostrilL) / tipSpacing;
    this.originX = this.bounds().min;
    // Textes dans la langue active : UI + messages du thème.
    this.ui = UI[lang];
    this.successMsgs = successMessages(lang, theme.key);
    this.failMsgs = failMessages(lang, theme.key);
    this.timeLeft = this.gameMode.duration ?? 0;
    this.best = loadBest(this.bestKey());
  }

  /** Clé de record : par thème pour Classique (compat), par thème+mode sinon. */
  private bestKey(): string {
    return this.gameMode.key === "classic"
      ? this.theme.key
      : `${this.theme.key}.${this.gameMode.key}`;
  }

  private get finger(): Finger {
    return this.theme.fingers[this.fingerIndex]!;
  }

  /** Bornes du va-et-vient : la pointe principale balaie SWEEP_MIN..SWEEP_MAX. */
  private bounds(): { min: number; max: number } {
    const tip0 = this.finger.tips[0]! * this.fingerScale;
    return { min: SWEEP_MIN - tip0, max: SWEEP_MAX - tip0 };
  }

  /** Position écran de chaque pointe pour un bord gauche donné. */
  private tipsAt(originX: number): number[] {
    return this.finger.tips.map((t) => originX + t * this.fingerScale);
  }

  /** Une pointe vise-t-elle cette narine ? */
  private static nearNostril(tipX: number, nostril: number): boolean {
    return Math.abs(tipX - nostril) <= TOLERANCE;
  }

  /** Transform courant du nez (translation dx, rotation a rad, échelle sc) autour
   *  du pivot haut-milieu (px,py). Figé pendant le coup pour aligner doigt/narine. */
  private noseXform(): { dx: number; a: number; sc: number; px: number; py: number } {
    const e = this.mode === "poke" ? this.pokeFreezeE : this.elapsed;
    const m = this.gameMode;
    const px = (this.theme.nostrilL + this.theme.nostrilR) / 2;
    const dx = m.noseSway ? m.noseSway * Math.sin((e * 2 * Math.PI) / NOSE_SWAY_PERIOD) : 0;
    const a = m.noseTilt
      ? ((m.noseTilt * Math.PI) / 180) * Math.sin((e * 2 * Math.PI) / NOSE_TILT_PERIOD)
      : 0;
    const sc = m.noseZoom
      ? 1 - m.noseZoom * (0.5 - 0.5 * Math.cos((e * 2 * Math.PI) / NOSE_TILT_PERIOD))
      : 1;
    return { dx, a, sc, px, py: NOSE_PIVOT_Y };
  }

  /** Transforme un point local du nez vers l'écran (échelle+rotation autour du
   *  pivot, puis translation). */
  private xformPoint(lx: number, ly: number): { x: number; y: number } {
    return transformPoint(lx, ly, this.noseXform());
  }

  /** x écran des deux narines (transformées par l'oscillation du mode). */
  private nostrilX(): { l: number; r: number } {
    const ny = this.theme.nostrilY;
    return {
      l: this.xformPoint(this.theme.nostrilL, ny).x,
      r: this.xformPoint(this.theme.nostrilR, ny).x,
    };
  }

  /** La tentative à cette position est-elle réussie ? */
  private isHit(originX: number): boolean {
    const tips = this.tipsAt(originX);
    const { l: nostrilL, r: nostrilR } = this.nostrilX();
    if (this.finger.tips.length === 1) {
      const x = tips[0]!;
      return Game.nearNostril(x, nostrilL) || Game.nearNostril(x, nostrilR);
    }
    // deux doigts : pointe gauche dans narine gauche ET pointe droite dans narine droite
    return Game.nearNostril(tips[0]!, nostrilL) && Game.nearNostril(tips[1]!, nostrilR);
  }

  /** Justesse d'un coup réussi : distance de la pointe au centre de la narine visée
   *  → 3 (PARFAIT), 2 (BIEN), 1 (OK). Pour 2 doigts, moyenne des deux écarts. */
  private hitPrecision(originX: number): { pts: number; label: string } {
    const tips = this.tipsAt(originX);
    const { l: nostrilL, r: nostrilR } = this.nostrilX();
    const d =
      this.finger.tips.length === 1
        ? Math.min(Math.abs(tips[0]! - nostrilL), Math.abs(tips[0]! - nostrilR))
        : (Math.abs(tips[0]! - nostrilL) + Math.abs(tips[1]! - nostrilR)) / 2;
    const pts = precisionPoints(d, TOLERANCE);
    const label = pts === 3 ? this.ui.perfect : pts === 2 ? this.ui.good : this.ui.ok;
    return { pts, label };
  }

  /** Frames de réaction du nez pour un raté, ou null si le(s) doigt(s) passe(nt)
   *  à CÔTÉ du nez (pas de réaction). 2 doigts → les deux narines ; 1 doigt →
   *  narine droite ou gauche selon le côté du nez touché. */
  private missReaction(originX: number): SpriteName[] | null {
    const { reactions } = this.theme;
    const ny = this.theme.nostrilY;
    const { l: nostrilL, r: nostrilR } = this.nostrilX();
    const noseLeftX = this.xformPoint(this.theme.noseLeft, ny).x;
    const noseRightX = this.xformPoint(this.theme.noseRight, ny).x;
    const onNose = (x: number) => x >= noseLeftX && x <= noseRightX;
    const tips = this.tipsAt(originX);
    if (this.finger.tips.length === 2) {
      // 2 doigts : réaction seulement si au moins un doigt touche le nez
      return tips.some(onNose) ? reactions.both : null;
    }
    const tipX = tips[0]!;
    if (!onNose(tipX)) return null; // à côté du nez → pas de réaction
    return tipX > (nostrilL + nostrilR) / 2 ? reactions.right : reactions.left;
  }

  /** Multiplicateur de vitesse : quadratique f(p)=SPEED_START+a·p+b·p² passant par
   *  les 3 points (p=0 → START, p=0.5 → MID, p=1 → END). */
  private timeFactor(): number {
    // Progression 0→1 selon le mode :
    //  - "streak" : avec la série (retombe à 0 à chaque raté) → Série jouable ;
    //  - sinon (temps) : sur la durée si chronométré, sinon sur 45 s (Survie).
    let p: number;
    if (this.gameMode.accelBy === "streak") {
      p = Math.min(1, this.streak / (this.gameMode.streakGoal ?? 12));
    } else {
      const span = this.gameMode.duration ?? 45;
      p = Math.min(1, this.elapsed / span);
    }
    return speedFactor(p, SPEED_START, SPEED_MID, SPEED_END);
  }

  /** La partie doit-elle se terminer ? (temps écoulé / plus de vies / série atteinte) */
  private checkOver(): boolean {
    const m = this.gameMode;
    if (m.duration !== null && this.timeLeft <= 0) return true;
    if (m.lives !== null && this.misses >= m.lives) return true;
    if (m.streakGoal !== null && this.streak >= m.streakGoal) {
      this.won = true;
      return true;
    }
    return false;
  }

  /** Termine la partie : enregistre le record et passe à l'écran de fin. */
  private endGame(): void {
    this.over = true;
    this.msgTimer = 0;
    if (this.score > this.best) {
      this.best = this.score;
      this.newRecord = true;
      saveBest(this.bestKey(), this.best);
    }
    if (this.newRecord) sfx.record();
    else if (this.won) sfx.win();
    else sfx.over();
    this.onGameOver?.({ score: this.score, theme: this.theme.key, mode: this.gameMode.key });
  }

  /** Réinitialise une partie (aussi appelé par l'overlay de fin : « REJOUER »). */
  reset(): void {
    this.mode = "run";
    this.dir = 1;
    this.speed = START_SPEED;
    this.fingerIndex = 0;
    this.score = 0;
    this.misses = 0;
    this.streak = 0;
    this.combo = 1;
    this.gainLine = "";
    this.timeLeft = this.gameMode.duration ?? 0;
    this.elapsed = 0;
    this.won = false;
    this.over = false;
    this.exit = false;
    this.paused = false;
    this.pauseSel = 0;
    this.newRecord = false; // le record (this.best) est conservé d'une partie à l'autre
    this.recordFlashed = false;
    this.msg = "";
    this.msgTimer = 0;
    this.cooldown = 0;
    this.countdown = COUNTDOWN;
    this.slowMo = false; // boosters valables une seule manche
    this.shield = 0;
    this.boostIcon = null;
    this.pokeElapsed = 0;
    this.hit = false;
    this.originX = this.bounds().min;
  }

  /** Le joueur demande-t-il à revenir au choix des styles ? (lu par la boucle de jeu) */
  get wantsExit(): boolean {
    return this.exit;
  }

  /** Partie terminée (écran de fin affiché) ? + score courant — lus par game-screen. */
  get isOver(): boolean {
    return this.over;
  }
  get currentScore(): number {
    return this.score;
  }
  get isPaused(): boolean {
    return this.paused;
  }
  /** Continue proposable ? (partie perdue, pas une victoire de série). */
  get canContinue(): boolean {
    return this.over && !this.won;
  }
  // État de fin de partie — lu par l'overlay HTML (game-screen).
  get isWon(): boolean {
    return this.won;
  }
  get bestScore(): number {
    return this.best;
  }
  get isNewRecord(): boolean {
    return this.newRecord;
  }

  /** Retour au menu (bouton MENU de l'overlay de fin). */
  quit(): void {
    this.exit = true;
  }

  /** Boosters actifs pour LA manche en cours (consommés : non restaurés au reset). */
  setBoosts(b: { slow?: boolean; shield?: number; icon?: string }): void {
    this.slowMo = !!b.slow;
    this.shield = b.shield ?? 0;
    this.boostIcon = b.icon ?? null;
  }

  /** « Continue » : ressort de l'écran de fin et prolonge la partie (temps/vies
   *  restaurés), en gardant le score acquis. */
  revive(): void {
    if (!this.canContinue) return;
    this.over = false;
    this.newRecord = false;
    if (this.gameMode.duration !== null) {
      this.elapsed = Math.max(0, this.gameMode.duration - CONTINUE_TIME);
      this.timeLeft = CONTINUE_TIME;
    }
    if (this.gameMode.lives !== null) this.misses = 0;
    this.mode = "run";
    this.cooldown = 0;
    this.pokeElapsed = 0;
    this.hit = false;
    this.countdown = COUNTDOWN_RESUME;
  }

  /** Met en pause / reprend (touche Échap/P en jeu, ou bouton ⏸). Sans effet sur
   *  l'écran de fin. */
  togglePause(): void {
    if (this.over) return;
    if (this.paused) this.resume();
    else {
      this.paused = true;
      this.pauseSel = 0;
    }
  }

  /** Reprend la partie avec un court délai (la main repart avant que l'on puisse
   *  cliquer → pas de coup gratuit après une pause). */
  private resume(): void {
    this.paused = false;
    this.countdown = COUNTDOWN_RESUME; // petit 2·1 avant de reprendre la main
  }

  /** Une position logique est-elle sur le bouton ⏸ (coin haut-droit) ? */
  private inPauseButton(x: number, y: number): boolean {
    return Math.abs(x - PAUSE_BTN.x) <= PAUSE_BTN.hw && Math.abs(y - PAUSE_BTN.y) <= PAUSE_BTN.hh;
  }

  /** ▲▼ : déplace la sélection du menu pause. (La fin de partie est un overlay HTML.) */
  moveSelection(d: number): void {
    if (this.paused) this.pauseSel = (this.pauseSel + d + 3) % 3; // 3 options de pause
  }

  /** Survol à la position logique : surligne l'option du menu pause. */
  hover(_x: number, y: number): void {
    if (this.paused) {
      const i = PAUSE_OPT_Y.findIndex((oy) => Math.abs(y - oy) <= 9);
      if (i >= 0) this.pauseSel = i;
    }
  }

  /** Clic à la position logique (x,y). Pause → valide l'option cliquée ; en jeu →
   *  bouton ⏸ = pause, sinon tentative. Fin de partie → overlay HTML au-dessus. */
  pointer(x: number, y: number): void {
    if (this.over) return; // l'overlay HTML capte les clics de l'écran de fin
    if (this.paused) {
      const i = PAUSE_OPT_Y.findIndex((oy) => Math.abs(y - oy) <= 9);
      if (i >= 0) {
        this.pauseSel = i;
        this.confirmPause();
      } else if (this.inPauseButton(x, y)) this.togglePause(); // tap ⏸ = reprendre
      return;
    }
    if (this.inPauseButton(x, y)) this.togglePause();
    else this.attempt();
  }

  /** Valide l'option du menu pause. */
  private confirmPause(): void {
    if (this.pauseSel === 0) this.resume(); // reprendre
    else if (this.pauseSel === 1) this.reset(); // recommencer la manche
    else this.exit = true; // quitter → menu (HUB)
  }

  /** Tentative du joueur (clic / espace). */
  attempt(): void {
    if (this.over) return; // fin de partie : l'overlay HTML gère les actions
    if (this.paused) {
      this.confirmPause(); // valide l'option du menu pause
      return;
    }
    if (this.countdown > 0) return; // pas de coup pendant le décompte
    if (this.mode !== "run" || this.cooldown > 0) return;
    this.pokeFreezeE = this.elapsed; // fige les oscillations du nez pour ce coup
    this.mode = "poke";
    this.pokeElapsed = 0;
    this.pokeFxDone = false;
    this.pokeOriginX = this.originX;
    this.hit = this.isHit(this.originX);
    if (!this.hit) {
      if (this.shield > 0) {
        // Bouclier : le raté est pardonné (ni compteur, ni casse de série).
        this.shield--;
        this.msg = "🛡️";
        this.msgFail = false;
        this.msgTimer = MSG_TIME;
        sfx.poke();
      } else {
        this.misses++;
        this.streak = 0; // un raté casse la série
        this.combo = 1;
        this.showMessage(true);
        sfx.miss();
      }
    } else {
      sfx.poke(); // amorce au bon moment ; la réussite sonne à l'impact
    }
  }

  update(dt: number): void {
    if (this.over) return; // partie terminée : l'overlay HTML prend la main
    if (this.paused) return; // tout est figé pendant la pause

    // Décompte 3·2·1 : la main et le chrono ne démarrent qu'à la fin.
    if (this.countdown > 0) {
      this.countdown = Math.max(0, this.countdown - dt);
      return;
    }

    if (this.msgTimer > 0) this.msgTimer = Math.max(0, this.msgTimer - dt);

    // Temps écoulé (pilote l'accélération) + décompte si le mode est chronométré.
    this.elapsed += dt;
    if (this.gameMode.duration !== null) {
      this.timeLeft = Math.max(0, this.gameMode.duration - this.elapsed);
    }
    // Conditions de fin propres au mode (temps / vies / objectif de série).
    if (this.checkOver()) {
      this.endGame();
      return;
    }

    // Délai après un coup : seule l'action est bloquée, la main continue de glisser.
    if (this.cooldown > 0) this.cooldown = Math.max(0, this.cooldown - dt);

    if (this.mode === "run") {
      const step =
        this.speed * this.timeFactor() * this.gameMode.speedMult * (this.slowMo ? 0.8 : 1) * dt;
      if (this.gameMode.movement === "drift") {
        // Défilé : toujours vers la droite ; sorti à droite → réapparaît à gauche.
        this.originX += step;
        const handW = this.rend.textureWidth(this.finger.name) * this.fingerScale;
        if (this.originX > W) this.originX = -handW;
      } else {
        // Va-et-vient (défaut) : rebond aux bornes.
        const { min, max } = this.bounds();
        this.originX += this.dir * step;
        if (this.originX >= max) {
          this.originX = max;
          this.dir = -1;
        } else if (this.originX <= min) {
          this.originX = min;
          this.dir = 1;
        }
      }
      return;
    }

    this.pokeElapsed += dt;
    // Impact visuel : la pointe atteint son apogée (fin de la montée) → particules.
    if (!this.pokeFxDone && this.pokeElapsed >= POKE_DURATION * 0.4) {
      this.pokeFxDone = true;
      this.spawnImpactFx();
    }
    if (this.pokeElapsed >= POKE_DURATION) {
      if (this.hit) {
        this.streak++;
        this.combo = Math.min(MAX_COMBO, this.streak);
        const { pts, label } = this.hitPrecision(this.pokeOriginX);
        // Mode Sniper : seules les réussites PARFAIT rapportent (mais le coup reste
        // une réussite — la série continue, pas de raté).
        const effPts = this.gameMode.perfectOnly && pts < 3 ? 0 : pts;
        const gain = effPts * this.combo;
        this.score += gain;
        // Record battu EN cours de partie → flash doré (une seule fois).
        if (!this.recordFlashed && this.best > 0 && this.score > this.best) {
          this.recordFlashed = true;
          this.rend.recordFlash();
        }
        this.gainLine = `${label} +${gain}${this.combo > 1 ? ` ×${this.combo}` : ""}`;
        // En accélération "série", la vitesse vient du facteur (pas de montée par
        // réussite, sinon double accélération et série impossible).
        if (this.gameMode.accelBy !== "streak") {
          this.speed = Math.min(MAX_SPEED, this.speed + SPEED_STEP);
        }
        this.fingerIndex = (this.fingerIndex + 1) % this.theme.fingers.length;
        this.showMessage(false);
        sfx.score(pts, this.combo); // pitch selon précision (1/2/3) + combo
      }
      this.mode = "run";
      this.cooldown = COOLDOWN; // attendre légèrement avant le prochain coup
    }
  }

  /** Particules à l'impact : morve verte jaillissant de la narine visée (dorée
   *  si PARFAIT) ; nuage de poussière là où le doigt s'écrase sur un raté. */
  private spawnImpactFx(): void {
    const ny = this.theme.nostrilY;
    const tips = this.tipsAt(this.pokeOriginX);
    if (this.hit) {
      const kind = this.hitPrecision(this.pokeOriginX).pts === 3 ? "perfect" : "booger";
      for (const nostril of [this.theme.nostrilL, this.theme.nostrilR]) {
        const p = this.xformPoint(nostril, ny);
        if (tips.some((x) => Game.nearNostril(x, p.x))) {
          this.rend.burst(p.x, p.y, kind);
          // Coulure de morve (coords LOCALES : elle suit le nez qui tangue).
          this.rend.drip(nostril, ny, kind === "perfect" ? 1.25 : 1);
        }
      }
    } else {
      const peakY = ny - MISS_RISE; // hauteur où la pointe s'écrase sur le nez
      for (const x of tips) this.rend.burst(x, peakY, "dust");
      this.rend.shake(); // l'écran tremble : le raté se SENT
    }
  }

  /** Tire un index au hasard différent du précédent. */
  private static pickDifferent(length: number, last: number): number {
    if (length <= 1) return 0;
    let i = last;
    while (i === last) i = Math.floor(Math.random() * length);
    return i;
  }

  /** Affiche un message au hasard (différent du précédent) : échec (rouge) ou
   *  réussite (vert) selon `fail`. */
  private showMessage(fail: boolean): void {
    const pool = fail ? this.failMsgs : this.successMsgs;
    const last = fail ? this.lastFail : this.lastMsg;
    const i = Game.pickDifferent(pool.length, last);
    if (fail) this.lastFail = i;
    else this.lastMsg = i;
    this.msg = pool[i]!;
    this.msgFail = fail;
    this.msgTimer = MSG_TIME;
  }

  render(): void {
    const { nostrilY, restTipY, pokeDepth } = this.theme;
    let tipY = restTipY;
    let noseFrame: SpriteName = "nez_0";
    let originX = this.originX;

    if (this.mode === "poke") {
      originX = this.pokeOriginX;
      const t = this.pokeElapsed / POKE_DURATION;
      // Réussite : la pointe monte plus haut (pokeDepth) → s'enfonce dans la narine.
      // Raté : la pointe monte SUR le nez (MISS_RISE), il s'écrase sur la surface.
      const peakY = this.hit ? nostrilY - pokeDepth : nostrilY - MISS_RISE;
      // Montée → maintien → descente du doigt.
      if (t < 0.4) tipY = lerp(restTipY, peakY, easeOut(t / 0.4));
      else if (t < 0.7) tipY = peakY;
      else tipY = lerp(peakY, restTipY, easeIn((t - 0.7) / 0.3));

      // Réussi → le nez reste immobile, le doigt s'enfonce dans la narine.
      // Raté → le nez joue la réaction ciblée (narine gauche / droite / les deux).
      if (!this.hit && t >= 0.3 && t < 0.8) {
        const frames = this.missReaction(originX);
        if (frames) {
          const k = Math.min(frames.length - 1, Math.floor(((t - 0.3) / 0.5) * frames.length));
          noseFrame = frames[k]!;
        }
        // sinon (doigt à côté du nez) : le nez reste au repos
      }
    }

    // État complet de la frame → couche de rendu PixiJS.
    // occluded : repos/réussite = le doigt passe derrière le nez (masqué sauf aux
    // narines) ; raté = il s'écrase SUR le nez (dessiné par-dessus, sans masque).
    const { l, r } = this.nostrilX();
    const state: RenderState = {
      noseFrame,
      nose: this.noseXform(),
      fingerName: this.finger.name,
      fingerTips: this.finger.tips,
      fingerTipYInSprite: this.finger.tipY,
      fingerScale: this.fingerScale,
      originX,
      tipY,
      occluded: !(this.mode === "poke" && !this.hit),
      targets: [l, r].map((x) => ({ x, hot: this.nostrilHot(originX, x) })),
      hud: {
        score: this.score,
        misses: this.misses,
        lives: this.gameMode.lives,
        duration: this.gameMode.duration,
        timeLeft: this.timeLeft,
        best: this.best,
        boostIcon: this.boostIcon,
        streakGoal: this.gameMode.streakGoal,
        streak: this.streak,
        combo: this.combo,
        isRecord: this.best > 0 && this.score > this.best,
      },
      // Traînée d'étincelles dès le combo ×4 (dorée au combo max).
      trail:
        this.mode === "run" && this.combo >= 4
          ? {
              xs: this.tipsAt(this.originX),
              y: this.theme.restTipY,
              max: this.combo >= MAX_COMBO,
            }
          : null,
      msg:
        this.msgTimer > 0
          ? { text: this.msg, fail: this.msgFail, t: this.msgTimer / MSG_TIME, gain: this.gainLine }
          : null,
      pauseSel: this.paused ? this.pauseSel : null,
      countdown: this.countdown,
      over: this.over,
    };
    this.rend.draw(state);
  }

  /** Une pointe (n'importe laquelle) est-elle bien alignée sur cette narine ? */
  private nostrilHot(originX: number, nostril: number): boolean {
    if (this.mode !== "run") return false;
    return this.tipsAt(originX).some((x) => Game.nearNostril(x, nostril));
  }
}

// Meilleur score par thème, persisté en localStorage (best-effort : ignore les
// erreurs en navigation privée / stockage indisponible).
const BEST_KEY = "the-big-dig.best.";

function loadBest(themeKey: string): number {
  try {
    const v = Number(localStorage.getItem(BEST_KEY + themeKey));
    return Number.isFinite(v) && v > 0 ? v : 0;
  } catch {
    return 0;
  }
}

function saveBest(themeKey: string, value: number): void {
  try {
    localStorage.setItem(BEST_KEY + themeKey, String(value));
  } catch {
    /* stockage indisponible : on garde juste le record en mémoire */
  }
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
function easeOut(t: number): number {
  return 1 - (1 - t) * (1 - t);
}
function easeIn(t: number): number {
  return t * t;
}
